
































































































































import { Component, Vue, Watch } from "vue-property-decorator";
import { eachDayOfInterval, startOfWeek, endOfWeek, endOfMonth, startOfMonth, format, addMonths, addWeeks, isSameDay, parseISO, addSeconds, addDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import debounce from "debounce";
import { Circle2 } from "vue-loading-spinner";

import ShiftFilter from "@/layouts/DashboardLayoutComponents/ShiftFilter.vue";
import ShiftCard from "@/layouts/DashboardLayoutComponents/ShiftCard.vue";

import ShiftModal from "./ShiftModal.vue";
import AnnounceModal from "./AnnounceModal.vue";
import CopyModal from "./CopyModal.vue";
import DeleteModal from "./DeleteModal.vue";
import { getItem } from "@/models/session";
import shiftModel from "@/models/shift.model";

@Component({
	components: {
		ShiftFilter,
		ShiftModal,
		ShiftCard,
		AnnounceModal,
		CopyModal,
		DeleteModal,
		Circle2,
	},
})
class ShiftsCalendar extends Vue {
	shiftId: string = "";
	public user = { role: "" };
	shiftFilter: any = {};
	async updateFilter(filter: any) {
		this.shiftFilter = filter;
		this.shiftsLoading = true;
		await this.searchShifts();
	}

	hasPrintIntent = false;

	viewingUnit: any = {};

	calendarIcons = [
		{
			title: "Agendado",
			icon: "fa fa-calendar shift-title__icon",
		},
		{
			title: "Não realizado",
			icon: "fa fa-times shift-title__icon",
		},
		{
			title: "Realizado",
			icon: "fa fa-check shift-title__icon",
		},
		{
			title: "Aprovado",
			icon: "fa fa-check-double shift-title__icon",
		},
		{
			title: "Faturado",
			icon: "fa fa-dollar-sign shift-title__icon",
		},
		{
			title: "Anunciado",
			icon: "fa fa-bullhorn shift-title__icon",
		},
		{
			title: "Standby",
			icon: "fa fa-pause shift-title__icon",
		},
		{
			title: "Troca solicitada",
			icon: "fa fa-exchange-alt shift-title__icon",
		},
		{
			title: "Em progresso",
			icon: "fa fa-hourglass shift-title__icon",
		},
	];

	constructor() {
		super();
		this.searchShifts = debounce(this.searchShifts, 300);
	}

	async mounted() {
		this.shiftId = this.$route.params.shiftId;
		this.user = getItem("user");
		this.weekDaysText();
		window.onbeforeprint = () => {
			this.handlePrintIntent();
		};
	}

	created() {
		window.addEventListener("resize", this.weekDaysText);
	}
	destroyed() {
		window.removeEventListener("resize", this.weekDaysText);
	}

	handleUnitLoaded(unit: any) {
		this.viewingUnit = { ...unit };
	}

	handlePrintIntent() {
		console.log("DETECTED PRINT INTENT");
		if (this.hasPrintIntent) {
			this.hasPrintIntent = false;
		}
		this.hasPrintIntent = true;
	}

	// -- Shift Modal
	@Watch("$route")
	updateRoute(route: any) {
		this.shiftId = route.params.shiftId || "";
	}

	closeModal() {
		this.$router.push(".");
		this.loadShifts();
	}

	openShift(shift?: any, date?: Date) {
		if (this.user.role == "CLIENT") {
			return;
		}
		if (shift) {
			this.$router.push(`/escalas/calendario/${shift?.id}`);
		} else {
			this.$router.push(`/escalas/calendario/novo?unit=${this.shiftFilter.unit}&date=${date?.toISOString()}`);
		}
	}

	// -- Calendar
	weekDays: string[] = this.getDaysForWeek(new Date()).map(date => format(date, "EEEE", { locale: ptBR }));

	weekDaysText() {
		this.weekDays = this.getDaysForWeek(new Date()).map(date => format(date, "EEEE", { locale: ptBR }));
		if (window.innerWidth < 744) {
			this.weekDays = this.weekDays.map(day => {
				return day.slice(0, 3);
			});
		}
	}

	referenceDate = new Date();

	calendarMode: "WEEK" | "MONTH" = "MONTH";

	get calendarDays() {
		let days: any = [];
		if (this.calendarMode === "MONTH") {
			days = this.getDaysForMonth(this.referenceDate).map(date => ({
				date,
				shifts: this.shifts.filter(shift => isSameDay(parseISO(shift.startDateTime), date)),
			}));
		} else if (this.calendarMode === "WEEK") {
			days = this.getDaysForWeek(this.referenceDate).map(date => ({
				date,
				shifts: this.shifts.filter(shift => isSameDay(parseISO(shift.startDateTime), date)),
			}));
		}
		return days;
	}

	filterCalendarDays(sectorId: number, specialtyId: number) {
		return [...this.calendarDays].map((calendarDay: any) => {
			const copyOfCalendarDay: any = {};
			Object.assign(copyOfCalendarDay, calendarDay);
			copyOfCalendarDay.shifts = calendarDay.shifts.filter((shift: any) => shift["sector.id"] == sectorId && shift["sector.specialty.id"] == specialtyId);
			return copyOfCalendarDay;
		});
	}

	get currentMonth() {
		return format(this.referenceDate, "MMMM/yyyy", { locale: ptBR });
	}

	get filteredUnitSectors() {
		if (!this.viewingUnit.unitSectors) {
			return [];
		}

		let filteredSectors = this.viewingUnit.unitSectors;

		if (this.shiftFilter["sector.specialty"]) {
			filteredSectors = filteredSectors.filter(
				(unitSector: any) => unitSector["sector.specialty.id"] == this.shiftFilter["sector.specialty"]
			);
		}

		if (this.shiftFilter.sector) {
			filteredSectors = filteredSectors.filter(
				(unitSector: any) => unitSector["sector.id"] == this.shiftFilter.sector
			);
		}

		// Não filtra por setores que têm plantões - mostra calendário vazio quando não há plantões
		return filteredSectors;
	}

	@Watch("calendarMode")
	changedCalendarMode() {
		this.loadShifts();
	}

	changeReferenceDate(delta: number = 1) {
		if (this.calendarMode === "MONTH") {
			this.referenceDate = addMonths(this.referenceDate, delta);
		} else if (this.calendarMode === "WEEK") {
			this.referenceDate = addWeeks(this.referenceDate, delta);
		}
		this.loadShifts();
	}

	getDaysForMonth(date: Date): Date[] {
		return eachDayOfInterval({
			start: startOfWeek(startOfMonth(date), { weekStartsOn: 1 }),
			end: endOfWeek(endOfMonth(date), { weekStartsOn: 1 }),
		});
	}

	getDaysForWeek(date: Date): Date[] {
		return eachDayOfInterval({
			start: startOfWeek(date, { weekStartsOn: 1 }),
			end: endOfWeek(date, { weekStartsOn: 1 }),
		});
	}

	formatShiftPeriod(shift: any): string {
		const durationParts = shift.durationTime.split(":");
		const durationSeconds = parseInt(durationParts[0]) * 3600 + parseInt(durationParts[1]) * 60 + parseInt(durationParts[2]);

		const startDateTime = parseISO(shift.startDateTime);
		const endDateTime = addSeconds(startDateTime, durationSeconds);

		return `${format(startDateTime, "HH:mm")} - ${format(endDateTime, "HH:mm")}`;
	}

	shifts: any[] = [];
	shiftsLoading = false;

	async loadShifts() {
		this.shiftsLoading = true;
		await this.searchShifts();
	}

	async searchShifts() {
		if (!this.shiftFilter.unit) {
			return;
		}

		const searchParameters = {
			...this.shiftFilter,
			startDateTime: {
				$gte: this.calendarDays[0].date,
				$lt: addDays(this.calendarDays[this.calendarDays.length - 1].date, 1),
			},
			deletedAt: null,
		};
		this.shifts = await shiftModel.search(
			{
				search: searchParameters,
				sort: [{ field: "startDateTime", order: "ASC" }],
			},
			true,
		);

		this.shifts.forEach(shift => {
			this.$set(shift, "isSelected", false);
		});
		this.shiftsLoading = false;
	}

	// -- Action mode
	actionMode: "ANNOUNCE" | "COPY" | "DELETE" | "NONE" = "NONE";
	async selectActionMode(mode: "ANNOUNCE" | "COPY" | "DELETE" | "NONE") {
		if (this.user.role == "CLIENT") {
			return;
		}
		if (this.actionMode !== mode) {
			this.actionMode = mode;
		} else if (this.areShiftsSelected) {
			await this.confirmAction(this.actionMode);
		} else {
			this.actionMode = "NONE";
		}
	}

	async confirmAction(mode: string) {
		switch (mode) {
			case "ANNOUNCE":
				this.showAnnounceModal = true;
				break;
			case "COPY":
				this.showCopyModal = true;
				break;
			case "DELETE":
				this.showDeleteModal = true;
				break;
		}
	}

	selectAll(day?: any) {
		if (this.actionMode === "NONE") {
			return;
		}
		const shiftsToSelect = day ? this.shifts.filter(shift => isSameDay(parseISO(shift.startDateTime), day.date)) : this.shifts;
		const selected = !shiftsToSelect[0]?.isSelected;
		shiftsToSelect.forEach(shift => {
			shift.isSelected = selected;
		});
	}

	get areShiftsSelected() {
		return !!this.shifts.filter(shift => shift.isSelected).length;
	}

	get calendarDaysWithSelectedShifts() {
		return this.calendarDays
			.map((calendarDay: any) => ({
				...calendarDay,
				shifts: calendarDay.shifts.filter((shift: any) => shift.isSelected),
			}))
			.filter((calendarDay: any) => calendarDay.shifts.length);
	}

	// -- Announce shifts
	showAnnounceModal = false;
	closeAnnounceModal() {
		this.showAnnounceModal = false;
		this.actionMode = "NONE";
		this.loadShifts();
	}

	// -- Delete shifts
	showCopyModal = false;
	closeCopyModal() {
		this.showCopyModal = false;
		this.actionMode = "NONE";
		this.loadShifts();
	}

	// -- Delete shifts
	showDeleteModal = false;
	closeDeleteModal() {
		this.showDeleteModal = false;
		this.actionMode = "NONE";
		this.loadShifts();
	}
}

export default ShiftsCalendar;
